// src/store/slices/companySlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import companyApi from 'api/companyApi';

export const fetchCompanies = createAsyncThunk(
  'company/fetchCompanies',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await companyApi.getAll(page, pageSize, search);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load companies';
      return rejectWithValue(msg);
    }
  }
);

export const createCompany = createAsyncThunk(
  'company/createCompany',
  async (data, { rejectWithValue }) => {
    try {
      const response = await companyApi.create(data);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create company';
      return rejectWithValue(msg);
    }
  }
);

export const updateCompany = createAsyncThunk(
  'company/updateCompany',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await companyApi.update(id, data);
      return response;
    } catch (err) {
      const msg = err.response?.data?.name?.[0] || 'Failed to update company';
      return rejectWithValue(msg);
    }
  }
);

export const deleteCompany = createAsyncThunk(
  'company/deleteCompany',
  async (id, { rejectWithValue }) => {
    try {
      await companyApi.delete(id);
      return id;
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.data?.detail || 'Failed to delete company';
      return rejectWithValue(msg);
    }
  }
);

export const exportCompanies = createAsyncThunk(
  'company/exportCompanies',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await companyApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `companies_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

export const importPreview = createAsyncThunk(
  'company/importPreview',
  async (file, { rejectWithValue }) => {
    try {
      const data = await companyApi.importPreview(file);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Preview failed');
    }
  }
);

export const importConfirm = createAsyncThunk(
  'company/importConfirm',
  async (preview, { rejectWithValue, dispatch }) => {
    try {
      await companyApi.importConfirm(preview);
      dispatch(fetchCompanies({ page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Import failed');
    }
  }
);

const companySlice = createSlice({
  name: 'company',
  initialState: {
    companies: [],
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createCompany.fulfilled, (state, action) => {
        state.companies.unshift(action.payload);
        state.pagination.count += 1;
      })

      .addCase(updateCompany.fulfilled, (state, action) => {
        const idx = state.companies.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.companies[idx] = action.payload;
      })

      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.companies = state.companies.filter((c) => c.id !== action.payload);
        state.pagination.count -= 1;
      })

      .addCase(exportCompanies.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError } = companySlice.actions;
export default companySlice.reducer;